import React, { useEffect, useState, useContext } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import ConsistencyLogo from './ConsistencyLogo';
import { useProductSkin } from '@/contexts/ProductSkinContext';
import {
  Plus,
  BarChart3,
  User,
  Shield,
  ChevronDown,
  Settings,
  LogOut,
  AlertCircle,
  Link2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useLegalLinks, getLegalUrl, LEGAL_SLUGS } from '@/hooks/useLegalLinks';

const API = `${window.__BACKEND_URL}/api`;
const API_URL = window.__BACKEND_URL;

export default function NewDashboardLayout() {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('create');
  const [hasCreator, setHasCreator] = useState(false);
  const [loadingCreators, setLoadingCreators] = useState(true);
  const { documents: footerLinks } = useLegalLinks('footer');

  // Footer: only show core legal docs (Terms, Privacy, Cookies)
  const CORE_FOOTER_SLUGS = [LEGAL_SLUGS.TERMS, LEGAL_SLUGS.PRIVACY, LEGAL_SLUGS.COOKIE];
  const coreFooterLinks = footerLinks.filter(doc => CORE_FOOTER_SLUGS.includes(doc.slug));
  const { skin } = useProductSkin();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const mainSections = [
    { id: 'create', label: 'CREATE', icon: Plus, path: '/app/create', gated: true },
    { id: 'analyze', label: 'ANALYZE', icon: BarChart3, path: '/app/analyze' },
    { id: 'connect', label: 'CONNECT', icon: Link2, path: '/app/connect' },
    { id: 'account', label: 'ACCOUNT', icon: User, path: '/app/account' }
  ];

  if (user?.role === 'admin' || user?.is_internal_admin) {
    mainSections.push({
      id: 'admin',
      label: 'ADMIN',
      icon: Shield,
      path: '/app/admin'
    });
  }

  useEffect(() => {
    const sectionFromPath = mainSections.find((section) =>
      location.pathname.startsWith(`/app/${section.id}`)
    );
    if (sectionFromPath) {
      setActiveSection(sectionFromPath.id);
    }
  }, [location.pathname]);

  useEffect(() => {
    let isMounted = true;
    const loadCreators = async () => {
      if (!user) {
        setLoadingCreators(false);
        setHasCreator(false);
        return;
      }

      try {
        const res = await fetch(`${API}/creators`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const creators = Array.isArray(data) ? data : Array.isArray(data?.creators) ? data.creators : [];
          if (isMounted) {
            setHasCreator(creators.length > 0);
          }
        } else {
          if (isMounted) setHasCreator(false);
        }
      } catch (error) {
        if (isMounted) setHasCreator(false);
      } finally {
        if (isMounted) setLoadingCreators(false);
      }
    };

    loadCreators();
    return () => {
      isMounted = false;
    };
  }, [user]);

  const isActiveSection = (sectionId) => {
    return location.pathname.startsWith(`/app/${sectionId}`);
  };

  const gatedNotice =
    !loadingCreators && !hasCreator ? (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 text-yellow-300 text-sm">
        <AlertCircle className="h-4 w-4" />
        <span>
          Create & Publish unlock after you add a Creator. Go to{' '}
          <Link className="underline text-white" to="/app/account/creator-profile">
            Account → Creator Profile
          </Link>
          .
        </span>
      </div>
    ) : null;

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col">
      <header className="backdrop-blur-sm bg-brand-bg/80 border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/app/create" className="flex items-center transition-opacity hover:opacity-80">
              {skin?.app_logo_url ? (
                <img 
                  src={`${API_URL}${skin.app_logo_url}`} 
                  alt="Logo" 
                  className="h-8 w-auto object-contain"
                  data-testid="header-logo"
                />
              ) : (
                <ConsistencyLogo variant="wordmark" size="md" />
              )}
            </Link>

            <nav className="flex items-center gap-2">
              {mainSections.map((section) => {
                // Skip gated sections only if user has no creator AND is not internal admin
                if (section.gated && !hasCreator && !user?.is_internal_admin) {
                  return null;
                }
                const Icon = section.icon;
                const active = isActiveSection(section.id);
                return (
                  <Link
                    key={section.id}
                    to={section.path}
                    className={`
                      flex items-center gap-2 px-4 py-2.5 rounded-lg
                      font-medium text-sm tracking-wide
                      transition-all duration-150
                      ${active ? 'bg-brand-blue text-white shadow-sm' : 'text-brand-text-muted hover:text-brand-text hover:bg-white/5'}
                    `}
                  >
                    <Icon className="h-4 w-4" />
                    {section.label}
                  </Link>
                );
              })}
            </nav>

            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-2 bg-brand-surface rounded-lg border border-white/5 hover:border-white/10 transition-colors">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold bg-gradient-to-br from-brand-blue to-brand-teal">
                      {user?.name?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <div className="hidden sm:block">
                      <p className="text-sm text-brand-text font-medium">{user?.name || 'User'}</p>
                      <p className="text-xs text-brand-text-muted capitalize">{user?.plan_tier || 'starter'}</p>
                    </div>
                    <ChevronDown className="h-4 w-4 text-brand-text-muted" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 glass border-white/10">
                  <DropdownMenuItem asChild>
                    <Link to="/app/account" className="flex items-center gap-2 cursor-pointer">
                      <User className="h-4 w-4" />
                      Account
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/app/account/settings" className="flex items-center gap-2 cursor-pointer">
                      <Settings className="h-4 w-4" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="flex items-center gap-2 cursor-pointer text-red-400 focus:text-red-400"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {gatedNotice && (
        <div className="px-6 pt-3">
          <div className="container mx-auto">{gatedNotice}</div>
        </div>
      )}

      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer with legal links */}
      <footer className="border-t border-white/10 py-4 mt-auto">
        <div className="container mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {skin?.symbol_url && (
              <img 
                src={`${API_URL}${skin.symbol_url}`} 
                alt="Symbol" 
                className="h-5 w-5 object-contain"
                data-testid="footer-symbol"
              />
            )}
            <p className="text-xs text-brand-text-muted">
              {skin?.footer_text || `© ${new Date().getFullYear()} Consistency. All rights reserved.`}
            </p>
          </div>
          {coreFooterLinks.length > 0 && (
            <nav className="flex items-center gap-4" data-testid="dashboard-footer-legal-links">
              {coreFooterLinks.map((doc) => (
                <Link
                  key={doc.slug}
                  to={getLegalUrl(doc.slug)}
                  className="text-xs text-brand-text-muted hover:text-brand-text transition-colors"
                >
                  {doc.title}
                </Link>
              ))}
            </nav>
          )}
        </div>
      </footer>
    </div>
  );
}